import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { LogOut, Mail, User, CreditCard, Download, Sparkles, HelpCircle, FileText, Shield, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import ExportModal from '../components/ExportModal';
import HelpModal from '../components/HelpModal';
import FAQSection from '../components/FAQSection';
import axios from 'axios';
import { format, addMonths } from 'date-fns';
import { AnimatedLogoRolodex } from '../components/ui/animated-logo-rolodex';
import ThemeToggle from '../components/ThemeToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const Settings = () => {
  const { user, logout, subscriptionInfo } = useAuth();
  const navigate = useNavigate();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [predictionEnabled, setPredictionEnabled] = useState(true);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  useEffect(() => {
    const handleOpenExport = () => {
      setExportModalOpen(true);
    };

    const handleOpenHelpModal = () => {
      setHelpModalOpen(true);
    };

    window.addEventListener('openExportModal', handleOpenExport);
    window.addEventListener('openHelpModal', handleOpenHelpModal);
    
    // Fetch prediction settings
    fetchPredictionSettings();
    
    return () => {
      window.removeEventListener('openExportModal', handleOpenExport);
      window.removeEventListener('openHelpModal', handleOpenHelpModal);
    };
  }, []);
  
  const fetchPredictionSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/prediction`);
      setPredictionEnabled(response.data.prediction_enabled);
    } catch (error) {
      console.error('Failed to fetch prediction settings:', error);
    }
  };
  
  const togglePrediction = async (enabled) => {
    try {
      await axios.put(`${API}/settings/prediction`, {
        prediction_enabled: enabled
      });
      setPredictionEnabled(enabled);
      toast.success(enabled ? 'AI predictions enabled' : 'AI predictions disabled');
    } catch (error) {
      console.error('Failed to update prediction settings:', error);
      toast.error('Failed to update settings');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const loadingToast = toast.loading('Opening subscription portal...');
      const response = await axios.post(`${API}/subscription/create-portal-session`);
      
      toast.dismiss(loadingToast);
      
      // Redirect to Stripe Customer Portal
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('No portal URL received');
      }
    } catch (error) {
      console.error('Failed to open subscription portal:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to open subscription portal';
      
      // User-friendly error messages
      if (errorMessage.includes('No subscription found')) {
        toast.error('Please subscribe first to manage your subscription');
        setTimeout(() => navigate('/subscribe'), 2000);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/auth');
  };

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-slate-900 dark:to-slate-800 transition-colors">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-md border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AnimatedLogoRolodex size="small" />
              <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }} data-testid="page-title">Settings</h1>
            </div>
            <Button
              onClick={() => setHelpModalOpen(true)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <HelpCircle className="w-4 h-4" />
              Help
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Theme Toggle */}
        <ThemeToggle />
        
        {/* Export Section */}
        <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors" data-testid="export-section">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-900 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>
              <Download className="w-5 h-5 mr-2" />
              Export Data
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4 transition-colors">Download your time entries as CSV or PDF</p>
            <Button
              onClick={() => setExportModalOpen(true)}
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
              data-testid="open-export-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Timesheet
            </Button>
          </div>
        </Card>

        {/* AI Project Predictor */}
        <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors" data-testid="ai-predictor-section">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-900 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>
              <Sparkles className="w-5 h-5 mr-2 text-purple-500 dark:text-purple-400" />
              AI Project Predictor
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium text-gray-900 dark:text-gray-100 transition-colors">Smart Predictions</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">AI suggests projects based on your patterns</p>
                </div>
                <button
                  onClick={() => togglePrediction(!predictionEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    predictionEnabled ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                  data-testid="prediction-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      predictionEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {predictionEnabled && (
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-4 rounded-lg transition-colors">
                  <p className="text-sm text-gray-700 dark:text-purple-200 transition-colors">
                    <strong>How it works:</strong><br />
                    • Learns from your time tracking patterns<br />
                    • Suggests projects based on time, keywords, and attendees<br />
                    • Gets smarter as you use it<br />
                    • Auto-selects projects when confidence is high
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Account Section */}
        <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors" data-testid="account-section">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-900 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>
              <User className="w-5 h-5 mr-2" />
              Account
            </h2>
            <div className="space-y-4">
              <div>
                <Label className="text-gray-600 dark:text-gray-400 transition-colors">Email</Label>
                <div className="flex items-center mt-1 text-gray-800 dark:text-gray-200 font-medium transition-colors">
                  <Mail className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                  {user?.email}
                </div>
              </div>
              
              {/* Reset Password Button */}
              <div>
                <Label className="text-gray-600 dark:text-gray-400 mb-2 block transition-colors">Password</Label>
                <Button
                  onClick={() => navigate('/forgot-password')}
                  variant="outline"
                  className="w-full md:w-auto border-cyan-300 dark:border-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
                  data-testid="reset-password-button"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Reset Password via Email
                </Button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 transition-colors">
                  You'll receive an email with instructions to reset your password
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Subscription Section */}
        <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors" data-testid="subscription-section">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-900 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>
              <CreditCard className="w-5 h-5 mr-2" />
              Subscription
            </h2>
            <div className="space-y-4">
              {subscriptionInfo?.status === 'trial' && (
                <div>
                  <div className="inline-block px-4 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg font-medium border border-orange-200 dark:border-orange-800 transition-colors">
                    Free Trial
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mt-2 transition-colors">
                    {subscriptionInfo.trial_days_left} days remaining in your trial
                  </p>
                  <Button
                    onClick={() => navigate('/subscribe')}
                    className="mt-4 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
                    data-testid="upgrade-button"
                  >
                    Upgrade to Pro - $2.99/month
                  </Button>
                </div>
              )}
              {subscriptionInfo?.status === 'active' && (
                <div>
                  <div className="inline-block px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg font-medium border border-green-200 dark:border-green-800 transition-colors">
                    Pro Subscription
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mt-2 transition-colors">$2.99/month</p>
                  {subscriptionInfo.next_billing_date && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 transition-colors">
                      Next billing date: {format(new Date(subscriptionInfo.next_billing_date), 'MMM dd, yyyy')}
                    </p>
                  )}
                  <Button
                    onClick={handleManageSubscription}
                    variant="outline"
                    className="mt-4 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    data-testid="manage-subscription-button"
                  >
                    Manage Subscription
                  </Button>
                </div>
              )}
              {subscriptionInfo?.status === 'cancelled' && (
                <div>
                  <div className="inline-block px-4 py-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-lg font-medium border border-yellow-200 dark:border-yellow-800 transition-colors">
                    Subscription Cancelled
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mt-2 transition-colors">Active until {subscriptionInfo.subscription_end_date && format(new Date(subscriptionInfo.subscription_end_date), 'MMM dd, yyyy')}</p>
                  {subscriptionInfo.subscription_end_date && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1 font-medium transition-colors">
                      ⚠️ {Math.ceil((new Date(subscriptionInfo.subscription_end_date) - new Date()) / (1000 * 60 * 60 * 24))} days remaining
                    </p>
                  )}
                  <Button
                    onClick={handleManageSubscription}
                    className="mt-4 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
                    data-testid="reactivate-button"
                  >
                    Reactivate Subscription
                  </Button>
                </div>
              )}
              {subscriptionInfo?.status === 'trial_expired' && (
                <div>
                  <div className="inline-block px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg font-medium border border-red-200 dark:border-red-800 transition-colors">
                    Trial Expired
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mt-2 transition-colors">Subscribe to continue using Logly</p>
                  <Button
                    onClick={() => navigate('/subscribe')}
                    className="mt-4 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
                  >
                    Subscribe Now - $2.99/month
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* FAQ Section */}
        <FAQSection />

        {/* Legal Section */}
        <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors" data-testid="legal-section">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>Legal</h2>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/privacy')}
                variant="outline"
                className="w-full justify-start text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                data-testid="privacy-policy-button"
              >
                <Shield className="w-4 h-4 mr-2" />
                Privacy Policy
              </Button>
              <Button
                onClick={() => navigate('/termsofuse')}
                variant="outline"
                className="w-full justify-start text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                data-testid="terms-button"
              >
                <FileText className="w-4 h-4 mr-2" />
                Terms of Service
              </Button>
            </div>
          </div>
        </Card>

        {/* Documentation Section */}
        <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>📚 Resources</h2>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/docs')}
                className="w-full justify-start bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
              >
                <FileText className="w-4 h-4 mr-2" />
                View Full Documentation
              </Button>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 transition-colors">
                Learn about all features including budgets, timelines, tags, and more.
              </p>
            </div>
          </div>
        </Card>

        {/* Admin Section - Only visible to admins */}
        {(user?.is_admin || user?.subscription_status === 'admin') && (
          <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors border-l-4 border-l-purple-500">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-900 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>
                <Shield className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
                Admin
              </h2>
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Access admin tools to manage users and view platform statistics.
                </p>
                <Button
                  onClick={() => navigate('/admin')}
                  className="w-full md:w-auto bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Open Admin Dashboard
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* About Section */}
        <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>About</h2>
            <div className="space-y-2 text-gray-600 dark:text-gray-400 transition-colors">
              <p>Logly v1.0.0</p>
              <p className="text-sm">Voice-first time tracking made simple</p>
            </div>
          </div>
        </Card>

        {/* Logout */}
        <Button
          onClick={handleLogout}
          variant="destructive"
          className="w-full bg-red-600 dark:bg-red-700 hover:bg-red-700 dark:hover:bg-red-600 text-white transition-colors"
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Log Out
        </Button>
      </div>

      {/* Export Modal */}
      <ExportModal open={exportModalOpen} onClose={() => setExportModalOpen(false)} />

      {/* Help Modal */}
      <HelpModal open={helpModalOpen} onClose={() => setHelpModalOpen(false)} />

      <BottomNav active="settings" />
    </div>
  );
};

export default Settings;
